/**
 * Единый сервис для работы с данными HR системы
 * Теперь работает с серверным API вместо browser SQLite
 * 
 * УМНОЕ РАЗДЕЛЕНИЕ API:
 * - Публичный API для чтения данных (GET операции)
 * - Защищенный API для админских операций (POST/PUT/DELETE)
 */

import { apiService, secureApiService } from './apiService'
import { devLog, devWarn, devError } from '../utils/devLog'

class DataService {
  constructor() {
    this.isInitialized = false
    this.initializationPromise = null
    this.cache = {
      departments: null,
      positions: null,
      transitions: null,
      lastUpdate: null
    }
    // Публичный API для чтения
    this.publicApi = apiService
    // Защищенный API для CRUD операций
    this.secureApi = secureApiService
    
    this.init()
  }

  async init() {
    // Избегаем множественной инициализации
    if (this.initializationPromise) {
      return this.initializationPromise
    }

    this.initializationPromise = this._performInit()
    return this.initializationPromise
  }

  async _performInit() {
    try {
      // Инициализируем публичный API для чтения данных
      const success = await this.publicApi.init()
      
      if (success) {
        this.isInitialized = true
        devLog('✅ DataService инициализирован с умным API разделением')
        
        // Предзагружаем основные данные в кэш через публичный API
        await this.preloadData()
        return true
      } else {
        throw new Error('Не удалось инициализировать публичный API сервис')
      }
    } catch (error) {
      devError('❌ Ошибка инициализации DataService:', error)
      this.isInitialized = false
      this.initializationPromise = null // Позволяем повторную попытку
      throw error
    }
  }

  /**
   * Ожидание инициализации с таймаутом
   */
  async waitForInitialization(timeout = 30000) {
    if (this.isInitialized) {
      return true
    }

    // Если инициализация не началась, запускаем
    if (!this.initializationPromise) {
      this.init()
    }

    // Ожидаем с таймаутом
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Таймаут инициализации DataService')), timeout)
    })

    try {
      await Promise.race([this.initializationPromise, timeoutPromise])
      return this.isInitialized
    } catch (error) {
      devError('❌ Ошибка ожидания инициализации:', error)
      throw error
    }
  }

  /**
   * Предзагрузка основных данных в кэш через публичный API
   */
  async preloadData() {
    try {
      devLog('🔄 Предзагрузка данных в кэш через публичный API...')
      
      const [departments, positions, transitions] = await Promise.all([
        this.publicApi.getDepartments(),
        this.publicApi.getPositions(),
        this.publicApi.getTransitions()
      ])

      this.cache.departments = departments
      this.cache.positions = positions
      this.cache.transitions = transitions
      this.cache.lastUpdate = Date.now()
      
      devLog('✅ Данные загружены в кэш:', {
        departments: departments.length,
        positions: positions.length,
        transitions: transitions.length
      })
    } catch (error) {
      devError('❌ Ошибка предзагрузки данных:', error)
      throw error
    }
  }

  // === ОТДЕЛЫ ===

  /**
   * Получение всех отделов (публичное чтение)
   */
  async getDepartments() {
    if (!this.isInitialized) {
      await this.waitForInitialization()
    }

    try {
      return await this.publicApi.getDepartments()
    } catch (error) {
      devError('❌ Ошибка получения отделов:', error)
      throw error
    }
  }

  /**
   * Создание отдела (защищенная операция)
   */
  async createDepartment(department) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.createDepartment(department)
    
    // Сбрасываем кэш при изменении данных
    this.cache.lastUpdate = null
    
    return result
  }

  /**
   * Обновление отдела (защищенная операция)
   */
  async updateDepartment(id, department) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.updateDepartment(id, department)
    
    // Сбрасываем кэш
    this.cache.lastUpdate = null
    
    return result
  }

  /**
   * Удаление отдела (защищенная операция)
   */
  async deleteDepartment(id) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.deleteDepartment(id)
    
    // Сбрасываем кэш
    this.cache.lastUpdate = null
    
    return result
  }

  // === ДОЛЖНОСТИ ===

  /**
   * Получение всех должностей (публичное чтение)
   */
  async getPositions() {
    if (!this.isInitialized) {
      await this.waitForInitialization()
    }

    try {
      return await this.publicApi.getPositions()
    } catch (error) {
      devError('❌ Ошибка получения должностей:', error)
      throw error
    }
  }

  /**
   * Получение должности по ID (публичное чтение)
   */
  async getPosition(id) {
    if (!this.isInitialized) {
      await this.waitForInitialization()
    }

    try {
      return await this.publicApi.getPosition(id)
    } catch (error) {
      devError(`❌ Ошибка получения должности ${id}:`, error)
      throw error
    }
  }

  /**
   * Создание должности (защищенная операция)
   */
  async createPosition(position) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.createPosition(position)
    
    // Сбрасываем кэш
    this.cache.lastUpdate = null
    
    return result
  }

  /**
   * Обновление должности (защищенная операция)
   */
  async updatePosition(id, position) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.updatePosition(id, position)
    
    // Сбрасываем кэш
    this.cache.lastUpdate = null
    
    return result
  }

  /**
   * Удаление должности (защищенная операция)
   */
  async deletePosition(id) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    try {
      const result = await this.secureApi.deletePosition(id)
      
      // Сбрасываем кэш
      this.cache.lastUpdate = null
      
      return result
    } catch (error) {
      // Если ошибка содержит информацию о переходах, возвращаем её как часть результата
      if (error.transitions && Array.isArray(error.transitions) && error.transitions.length > 0) {
        return {
          success: false,
          error: error.error || error.message,
          transitions: error.transitions,
          position_title: error.position_title
        }
      }
      
      // Иначе прокидываем ошибку дальше
      throw error
    }
  }

  // === ПЕРЕХОДЫ ===

  /**
   * Получение всех переходов (публичное чтение)
   */
  async getTransitions() {
    if (!this.isInitialized) {
      await this.waitForInitialization()
    }

    try {
      return await this.publicApi.getTransitions()
    } catch (error) {
      devError('❌ Ошибка получения переходов:', error)
      throw error
    }
  }

  /**
   * Создание перехода (защищенная операция)
   */
  async createTransition(transition) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.createTransition(transition)
    
    // Сбрасываем кэш
    this.cache.lastUpdate = null
    
    return result
  }

  /**
   * Обновление перехода (защищенная операция)
   */
  async updateTransition(id, transition) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.updateTransition(id, transition)
    
    // Сбрасываем кэш
    this.cache.lastUpdate = null
    
    return result
  }

  /**
   * Удаление перехода (защищенная операция)
   */
  async deleteTransition(id) {
    if (!this.isInitialized) {
      throw new Error('DataService не инициализирован')
    }

    const result = await this.secureApi.deleteTransition(id)
    
    // Сбрасываем кэш
    this.cache.lastUpdate = null
    
    return result
  }

  // === ДОПОЛНИТЕЛЬНЫЕ МЕТОДЫ ===

  /**
   * Получение отделов с должностями (используется в матрице карьеры)
   * Публичное чтение через кэшированные данные
   */
  async getDepartmentsWithPositions() {
    if (!this.isInitialized) {
      await this.waitForInitialization()
    }

    try {
      // Проверяем кэш (возраст <= 5 минут)
      const cacheMaxAge = 5 * 60 * 1000
      const isCacheValid = this.cache.lastUpdate && (Date.now() - this.cache.lastUpdate < cacheMaxAge)

      if (!isCacheValid) {
        devLog('🔄 Кэш устарел, обновляем данные...')
        await this.preloadData()
      }

      // Группируем должности по отделам
      const departmentsWithPositions = this.cache.departments.map(department => ({
        ...department,
        positions: this.cache.positions.filter(position => position.department_id === department.id)
      }))

      return departmentsWithPositions
    } catch (error) {
      devError('❌ Ошибка получения отделов с должностями:', error)
      throw error
    }
  }

  /**
   * Получение карьерных путей (публичное чтение)
   */
  async getCareerPaths() {
    if (!this.isInitialized) {
      await this.waitForInitialization()
    }

    try {
      return await this.publicApi.getCareerPaths()
    } catch (error) {
      devError('❌ Ошибка получения карьерных путей:', error)
      throw error
    }
  }

  /**
   * Очистка кэша
   */
  clearCache() {
    this.cache = {
      departments: null,
      positions: null,
      transitions: null,
      lastUpdate: null
    }
    devLog('🧹 Кэш DataService очищен')
  }

  /**
   * Принудительное обновление кэша
   */
  async refreshCache() {
    if (this.isInitialized) {
      await this.preloadData()
    }
  }

  /**
   * Получение информации о состоянии сервиса
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      hasCache: !!this.cache.lastUpdate,
      cacheAge: this.cache.lastUpdate ? Date.now() - this.cache.lastUpdate : null,
      publicApiUrl: this.publicApi.baseUrl,
      secureApiUrl: this.secureApi.baseUrl
    }
  }
}

// Создаем единственный экземпляр
export const dataService = new DataService()
export default dataService 